import React from 'react'
// Data router (createBrowserRouter) rather than the component <BrowserRouter>
// so route-level guards like useBlocker work — required by Bug #302 to warn
// before navigating away from an in-progress assessment.
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
} from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import { useAuth } from '@/auth/useAuth'
import { Shell } from '@/components/shell/Shell'
import { UserRole } from '@/types/index'
// Feature #384: module on/off switch (Epic #381).
import { useAppStore, useAppEnabled } from '@/store/appStore'
import ModuleUnavailable from '@/pages/ModuleUnavailable'
import Login from '@/pages/Login'
import Home from '@/pages/Home'
import UserList from '@/pages/users/UserList'
import UserDetail from '@/pages/users/UserDetail'
import CompanyList from '@/pages/companies/CompanyList'
import CompanyDetail from '@/pages/companies/CompanyDetail'
import ProjectList from '@/pages/projects/ProjectList'
import ProjectDetail from '@/pages/projects/ProjectDetail'
import Notifications from '@/pages/Notifications'
import Profile from '@/pages/Profile'
import SearchResults from '@/pages/SearchResults'
import Settings from '@/pages/settings/Settings'
import AccessSettings from '@/pages/settings/AccessSettings'
import NavigationSettings from '@/pages/settings/NavigationSettings'
import NotificationSettings from '@/pages/settings/NotificationSettings'
import DataSettings from '@/pages/settings/DataSettings'
import AppSettings from '@/pages/settings/AppSettings'
import ApplicationList from '@/pages/tap/ApplicationList'
import ApplicationDetail from '@/pages/tap/ApplicationDetail'
import TapDashboard from '@/pages/tap/Dashboard'
import ScorecardTemplates from '@/pages/tap/admin/ScorecardTemplates'
import SlaSettings from '@/pages/tap/admin/SlaSettings'
import TapSettings from '@/pages/tap/admin/TapSettings'
import EmailTemplates from '@/pages/admin/EmailTemplates'
// Feature #262: Lab-wide automation framework — replaces the
// TAP-specific IntegrationSequences page that lived under
// /tap/admin/integration-sequences.
import AutomationSequences from '@/pages/admin/AutomationSequences'
import { AutomationRunDetail, AutomationRunList } from '@/pages/admin/AutomationRuns'
// Epic #281: Skills Management module.
import MySkills from '@/pages/sm/MySkills'
import MyAssessments from '@/pages/sm/MyAssessments'
import SmAssessmentForm from '@/pages/sm/AssessmentForm'
import SmStaffList from '@/pages/sm/StaffList'
import SmStaffDetail from '@/pages/sm/StaffDetail'
import SmReviewsPending from '@/pages/sm/ReviewsPending'
import SmReviewDetail from '@/pages/sm/ReviewDetail'
import SmCapabilities from '@/pages/sm/Capabilities'
import SmCoverage from '@/pages/sm/Coverage'
import SkillsCatalogue from '@/pages/sm/admin/SkillsCatalogue'
import SkillRequests from '@/pages/sm/admin/SkillRequests'
import CapabilityCatalogue from '@/pages/sm/admin/CapabilityCatalogue'
import SmSettings from '@/pages/sm/admin/SmSettings'

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ fontSize: '16px', color: 'var(--color-dark-grey)' }}>Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <Shell>{children}</Shell>
}

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ fontSize: '16px', color: 'var(--color-dark-grey)' }}>Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  const isPlatformAdmin = user?.roles?.includes(UserRole.PlatformAdmin)

  if (!isPlatformAdmin) {
    return <Navigate to="/" replace />
  }

  return <Shell>{children}</Shell>
}

// Feature #256: TAP Settings and the TAP admin sub-pages must be
// reachable by HR Officers (``manage_tap`` role) as well as platform
// admins. The platform-level ``AdminRoute`` above stays platform-admin-
// only so that cross-module settings (access groups, navigation,
// notifications, etc.) remain locked down — only the TAP-namespaced
// admin pages relax to MANAGE_TAP.
const TapAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ fontSize: '16px', color: 'var(--color-dark-grey)' }}>Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  const roles = user?.roles || []
  const allowed =
    roles.includes(UserRole.PlatformAdmin) || roles.includes(UserRole.ManageTap)

  if (!allowed) {
    return <Navigate to="/" replace />
  }

  return <Shell>{children}</Shell>
}

// Epic #281: SM route guards. Modelled on TapAdminRoute — the SM pages
// relax to the module role while platform-wide settings stay admin-only.
// Nav hiding (lab_nav_item.required_ous) is cosmetic; these guards are
// what actually enforce access on direct URL entry.
const SmManagerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ fontSize: '16px', color: 'var(--color-dark-grey)' }}>Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  const roles = user?.roles || []
  const allowed =
    roles.includes(UserRole.PlatformAdmin) || roles.includes(UserRole.SmManager)

  if (!allowed) {
    return <Navigate to="/" replace />
  }

  return <Shell>{children}</Shell>
}

const SmAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ fontSize: '16px', color: 'var(--color-dark-grey)' }}>Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  const roles = user?.roles || []
  const allowed =
    roles.includes(UserRole.PlatformAdmin) || roles.includes(UserRole.SmAdmin)

  if (!allowed) {
    return <Navigate to="/" replace />
  }

  return <Shell>{children}</Shell>
}

// Feature #359 — SM Coverage: pnc_officer or sm_manager (platform_admin
// always). sm_admin is deliberately excluded: the heatmap names people
// against ratings, and sm_admin confers no read of individual ratings.
const SmCoverageRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth()

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ fontSize: '16px', color: 'var(--color-dark-grey)' }}>Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  const roles = user?.roles || []
  const allowed =
    roles.includes(UserRole.PlatformAdmin) ||
    roles.includes(UserRole.PncOfficer) ||
    roles.includes(UserRole.SmManager)

  if (!allowed) {
    return <Navigate to="/" replace />
  }

  return <Shell>{children}</Shell>
}

// Feature #384 (Epic #381): module guard. Wraps a module's routes *outside*
// the role guards above, so role logic is untouched:
//   <AppRoute app="sm" name="Skills Management"><SmAdminRoute>…</SmAdminRoute></AppRoute>
//
// It answers one question — is this module switched on in this environment
// (Settings › Apps, lab_app) — and nothing else. Authentication is left to
// the inner guard: an unauthenticated visitor falls straight through to its
// /login redirect. While the registry is loading we show the same spinner
// the other guards use, and a module that is off renders ModuleUnavailable
// inside the Shell so the sidebar stays usable.
//
// The real enforcement is the backend gate (#383); this exists so a user
// who types /sm/my-skills on prod gets a plain sentence rather than a
// blank page and an API error toast.
const AppRoute: React.FC<{ app: string; name: string; children: React.ReactNode }> = ({
  app,
  name,
  children,
}) => {
  const { accounts } = useMsal()
  const isAuthenticated = accounts.length > 0
  const loaded = useAppStore((s) => s.loaded)
  const load = useAppStore((s) => s.load)
  const enabled = useAppEnabled(app)

  React.useEffect(() => {
    if (isAuthenticated) {
      void load()
    }
  }, [isAuthenticated, load])

  if (!isAuthenticated) {
    return <>{children}</>
  }

  if (!loaded) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ fontSize: '16px', color: 'var(--color-dark-grey)' }}>Loading...</div>
      </div>
    )
  }

  if (!enabled) {
    return (
      <Shell>
        <ModuleUnavailable moduleName={name} />
      </Shell>
    )
  }

  return <>{children}</>
}

const SmApp: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AppRoute app="sm" name="Skills Management">{children}</AppRoute>
)

const TapApp: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <AppRoute app="tap" name="Talent Acquisition">{children}</AppRoute>
)

const NotFoundPage: React.FC = () => (
  <Shell>
    <div style={{ textAlign: 'center', paddingTop: '60px' }}>
      <h1 style={{ fontSize: '32px', fontWeight: 600, color: 'var(--color-black)', marginBottom: '16px' }}>
        Page not found
      </h1>
      <p style={{ color: 'var(--color-dark-grey)', marginBottom: '32px' }}>
        The page you are looking for does not exist.
      </p>
      <a
        href="/"
        style={{
          display: 'inline-block',
          padding: '10px 20px',
          backgroundColor: 'var(--color-magenta)',
          color: 'var(--color-white)',
          textDecoration: 'none',
          borderRadius: '6px',
          fontWeight: 500,
        }}
      >
        Back home
      </a>
    </div>
  </Shell>
)

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route>
      <Route path="/login" element={<Login />} />
        <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />

        <Route path="/users" element={<ProtectedRoute><UserList /></ProtectedRoute>} />
        <Route path="/users/:id" element={<ProtectedRoute><UserDetail /></ProtectedRoute>} />

        <Route path="/companies" element={<ProtectedRoute><CompanyList /></ProtectedRoute>} />
        <Route path="/companies/:id" element={<ProtectedRoute><CompanyDetail /></ProtectedRoute>} />

        <Route path="/projects" element={<ProtectedRoute><ProjectList /></ProtectedRoute>} />
        <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />

        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/search" element={<ProtectedRoute><SearchResults /></ProtectedRoute>} />

        {/* Bug #368: email-template management is platform-admin only.
            Other roles still USE templates (the TAP compose modal reads
            the active list), which stays open on the backend. */}
        <Route path="/email-templates" element={<AdminRoute><EmailTemplates /></AdminRoute>} />

        {/* Skills Management (Epic #281). Every route sits inside
            <AppRoute app="sm"> (Feature #384): when the module is switched
            off in Settings › Apps the page gives way to ModuleUnavailable.
            The role guards inside are untouched. */}
        <Route path="/sm/my-skills" element={<SmApp><ProtectedRoute><MySkills /></ProtectedRoute></SmApp>} />
        {/* Feature #325: My Assessments is ProtectedRoute, NOT SmManagerRoute.
            Anyone can hold a draft self-assessment, so gating it the way the
            neighbouring SM routes are gated would hide it from most of the
            people it exists for. */}
        <Route path="/sm/assessments" element={<SmApp><ProtectedRoute><MyAssessments /></ProtectedRoute></SmApp>} />
        <Route path="/sm/assess/:userId" element={<SmApp><ProtectedRoute><SmAssessmentForm /></ProtectedRoute></SmApp>} />
        <Route path="/sm/staff" element={<SmApp><SmManagerRoute><SmStaffList /></SmManagerRoute></SmApp>} />
        <Route path="/sm/staff/:userId" element={<SmApp><SmManagerRoute><SmStaffDetail /></SmManagerRoute></SmApp>} />
        <Route path="/sm/coverage" element={<SmApp><SmCoverageRoute><SmCoverage /></SmCoverageRoute></SmApp>} />
        <Route path="/sm/reviews" element={<SmApp><SmManagerRoute><SmReviewsPending /></SmManagerRoute></SmApp>} />
        {/* Feature #340: the review DETAIL is ProtectedRoute, unlike the
            manager inbox above it. The subject and their manager work
            through the same screen — "all of these ranks and reasons are
            displayed to Person A as they perform the review" — and a review
            the subject cannot open is a conversation about them held over
            their shoulder. The API decides: it serves the review to the
            subject or an sm_manager and refuses everyone else, and flags
            the subject's copy `read_only` so no write control renders. */}
        <Route path="/sm/reviews/:reviewId" element={<SmApp><ProtectedRoute><SmReviewDetail /></ProtectedRoute></SmApp>} />
        {/* Feature #313: the catalogue is open to everyone — it evaluates
            against the caller unless a manager switches context, and the
            API enforces that, not this guard. */}
        <Route path="/sm/capabilities" element={<SmApp><ProtectedRoute><SmCapabilities /></ProtectedRoute></SmApp>} />
        <Route path="/sm/catalogue" element={<SmApp><SmAdminRoute><SkillsCatalogue /></SmAdminRoute></SmApp>} />
        {/* Feature #374 — the skill request queue feeds the catalogue, so it
            carries the same sm_admin gate. */}
        <Route path="/sm/skill-requests" element={<SmApp><SmAdminRoute><SkillRequests /></SmAdminRoute></SmApp>} />
        <Route path="/sm/admin/capabilities" element={<SmApp><SmAdminRoute><CapabilityCatalogue /></SmAdminRoute></SmApp>} />
        <Route path="/sm/settings" element={<SmApp><SmAdminRoute><SmSettings /></SmAdminRoute></SmApp>} />

        <Route path="/settings" element={<AdminRoute><Settings /></AdminRoute>} />
        <Route path="/settings/access" element={<AdminRoute><AccessSettings /></AdminRoute>} />
        <Route path="/settings/navigation" element={<AdminRoute><NavigationSettings /></AdminRoute>} />
        <Route path="/settings/notifications" element={<AdminRoute><NotificationSettings /></AdminRoute>} />
        <Route path="/settings/data" element={<AdminRoute><DataSettings /></AdminRoute>} />
        <Route path="/settings/apps" element={<AdminRoute><AppSettings /></AdminRoute>} />

        {/* Talent Acquisition Platform — inside <AppRoute app="tap">, see the
            SM block above (Feature #384). */}
        <Route path="/tap/applications" element={<TapApp><ProtectedRoute><ApplicationList /></ProtectedRoute></TapApp>} />
        <Route path="/tap/applications/:id" element={<TapApp><ProtectedRoute><ApplicationDetail /></ProtectedRoute></TapApp>} />
        {/* Bug #369: the dashboard endpoint is MANAGE_TAP-only, so a
            no-role user got a permission error here. /tap/applications
            stays ProtectedRoute — it also serves Shared with me. */}
        <Route path="/tap/dashboard" element={<TapApp><TapAdminRoute><TapDashboard /></TapAdminRoute></TapApp>} />
        {/* Feature #256: TAP-namespaced admin pages use TapAdminRoute so
            HR Officers (manage_tap) can manage them in addition to
            platform admins. */}
        <Route path="/tap/settings" element={<TapApp><TapAdminRoute><TapSettings /></TapAdminRoute></TapApp>} />
        <Route path="/tap/admin/scorecard-templates" element={<TapApp><TapAdminRoute><ScorecardTemplates /></TapAdminRoute></TapApp>} />
        <Route path="/tap/admin/sla-settings" element={<TapApp><TapAdminRoute><SlaSettings /></TapAdminRoute></TapApp>} />

        {/* Feature #262: Lab-wide automation framework. Lives under
            /automation/* (no longer TAP-namespaced) and is gated to
            platform_admin only per the Feature spec — automation
            sequences can touch any module's data so widening the
            audience would broaden the blast radius of a misconfigured
            run beyond what the previous TAP-only access model
            covered. */}
        <Route path="/automation/sequences" element={<AdminRoute><AutomationSequences /></AdminRoute>} />
        <Route path="/automation/sequences/:id/runs" element={<AdminRoute><AutomationRunList /></AdminRoute>} />
        <Route path="/automation/runs/:id" element={<AdminRoute><AutomationRunDetail /></AdminRoute>} />

        <Route path="*" element={<NotFoundPage />} />
    </Route>
  )
)

export default function App() {
  return <RouterProvider router={router} />
}
